import { Component, OnInit, OnDestroy, ChangeDetectionStrategy, ChangeDetectorRef, ViewChild, ElementRef } from '@angular/core';
import { Subject, fromEvent } from 'rxjs';
import { takeUntil, debounceTime, filter } from 'rxjs/operators';
import { UIStateService, PanelPosition } from '../services/ui-state.service';
import { PositionCalculatorService } from '../floating-inspector/position-calculator.service';
import { PropertyGrouperService } from '../floating-inspector/property-grouper.service';
import { BuilderScreenBetaService } from '../services/builder-screen.service';
import { BuilderNode } from '../interfaces/builder-screen.models';

export interface QuickEditProperty {
  key: string;
  displayName: string;
  type: 'text' | 'color' | 'select' | 'number' | 'boolean';
  value: any;
  options?: Array<{ label: string; value: any }>;
}

@Component({
  selector: 'app-inline-editor',
  templateUrl: './inline-editor.component.html',
  styleUrls: ['./inline-editor.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: false,
})
export class InlineEditorComponent implements OnInit, OnDestroy {
  @ViewChild('editorCard', { static: false }) editorCard!: ElementRef<HTMLDivElement>;

  private destroy$ = new Subject<void>();
  
  showInlineEditor = false;
  editorPosition: PanelPosition = { x: 0, y: 0, width: 280, height: 200 };
  quickEditProperties: QuickEditProperty[] = [];
  currentComponent: BuilderNode | null = null;
  
  // Detecção de clique duplo
  private clickCount = 0;
  private clickTimer: any;
  private editorOpenTime = 0;

  constructor(
    private uiState: UIStateService,
    private positionCalculator: PositionCalculatorService,
    private propertyGrouper: PropertyGrouperService,
    private builderService: BuilderScreenBetaService,
    private cdr: ChangeDetectorRef
  ) {}

  ngOnInit() {
    // Escuta eventos de clique duplo nos componentes do canvas
    this.setupDoubleClickListener();
    
    // Fecha editor inline ao clicar fora
    fromEvent(document, 'click').pipe(
      takeUntil(this.destroy$),
      filter(() => this.showInlineEditor),
      debounceTime(50) // Delay aumentado para prevenir fechamento imediato
    ).subscribe((event) => {
      try {
        // Ignora cliques por 300ms após abertura para prevenir fechamento imediato
        const timeSinceOpen = Date.now() - this.editorOpenTime;
        if (timeSinceOpen < 300) {
          return;
        }
        
        const isInside = this.isClickInsideEditor(event as MouseEvent);
        
        // Verificação de clique fora do editor
        if (!isInside) {
          // Clique detectado fora do editor
        }
        
        if (!isInside) {
          this.closeInlineEditor();
        }
      } catch (error) {
        console.warn('Error checking click inside editor:', error);
        // Fecha editor em caso de erro para prevenir estado travado
        this.closeInlineEditor();
      }
    });

    // Fecha com tecla escape
    fromEvent(document, 'keydown').pipe(
      takeUntil(this.destroy$),
      filter((event: any) => event.key === 'Escape' && this.showInlineEditor)
    ).subscribe(() => {
      this.closeInlineEditor();
    });
  }

  ngOnDestroy() {
    this.destroy$.next();
    this.destroy$.complete();
    if (this.clickTimer) {
      clearTimeout(this.clickTimer);
    }
  }

  private setupDoubleClickListener() {
    fromEvent(document, 'click').pipe(
      takeUntil(this.destroy$),
      debounceTime(10)
    ).subscribe((event) => {
      const target = event.target as HTMLElement;
      
      // Procura por nós do canvas ou elementos com data-component-id
      const componentElement = target.closest('[data-component-id]') as HTMLElement || 
                              target.closest('.canvas-node') as HTMLElement;
      
      if (componentElement) {
        this.handleComponentClick(componentElement, event as MouseEvent);
      }
    });
  }

  private handleComponentClick(componentElement: HTMLElement, event: MouseEvent) {
    // Procura por data-component-id no elemento ou seu pai canvas-node mais próximo
    let componentId = componentElement.getAttribute('data-component-id');
    let targetElement = componentElement;
    
    if (!componentId) {
      const canvasNode = componentElement.closest('.canvas-node');
      if (canvasNode) {
        // Obtém o ID do componente do manipulador de clique do nó do canvas ou encontra input com data-component-id
        const inputWithId = canvasNode.querySelector('[data-component-id]') as HTMLElement;
        if (inputWithId) {
          componentId = inputWithId.getAttribute('data-component-id');
          targetElement = canvasNode as HTMLElement;
        }
      }
    }
    
    if (!componentId) return;

    this.clickCount++;
    
    if (this.clickCount === 1) {
      this.clickTimer = setTimeout(() => {
        this.clickCount = 0;
      }, 300);
    } else if (this.clickCount === 2) {
      clearTimeout(this.clickTimer);
      this.clickCount = 0;
      
      // Previne propagação do evento para evitar fechamento imediato
      event.stopPropagation();
      event.preventDefault();
      
      // Clique duplo detectado - mostra editor inline
      this.showInlineEditorForComponent(componentId, targetElement, event);
    }
  }

  private showInlineEditorForComponent(componentId: string, element: HTMLElement, event: MouseEvent) {
    // CORREÇÃO CRÍTICA: Aguarda estado da UI ser adequadamente sincronizado antes de abrir Edição Rápida
    // Isso previne mostrar dados obsoletos quando componentes são rapidamente adicionados/selecionados
    setTimeout(() => {
      this.openQuickEditWithCorrectComponent(componentId, element, event);
    }, 50); // Pequeno delay para garantir sincronização do estado
  }
  
  private openQuickEditWithCorrectComponent(componentId: string, element: HTMLElement, event: MouseEvent) {
    // CORREÇÃO CRÍTICA: Verifica se Edição Rápida já está aberta para prevenir conflitos
    if (this.showInlineEditor) {
      this.closeInlineEditor();
      
      // Aguarda um pouco antes de abrir nova Edição Rápida
      setTimeout(() => {
        this.openQuickEditWithCorrectComponent(componentId, element, event);
      }, 100);
      return;
    }
    
    // Obtém o componente atualmente selecionado do estado da UI primeiro
    // Isso garante que sempre estamos trabalhando com a seleção mais atualizada
    const currentState = this.uiState.getCurrentState();
    const currentlySelectedId = currentState.inspector.selectedComponent;
        
    
    // LÓGICA APRIMORADA: Prefere seleção do estado da UI, mas valida se existe
    let targetComponentId = currentlySelectedId || componentId;
    
          // Obtém nós atuais para validar se o componente alvo existe
    this.builderService.nodes$.pipe(
      takeUntil(this.destroy$)
    ).subscribe(nodes => {
      // Valida se o componente alvo realmente existe
      let component = nodes.find(n => n.id === targetComponentId);
      
              // Se o componente do estado da UI não existe, volta para o componente do DOM
      if (!component && targetComponentId !== componentId) {
        console.warn(`⚠️ UI state component ${targetComponentId} not found, falling back to DOM component ${componentId}`);
        targetComponentId = componentId;
        component = nodes.find(n => n.id === targetComponentId);
      }
      
      if (!component) {
        console.warn(`❌ Component not found with ID: ${targetComponentId}`);
        return;
      }

      
      
      // CORREÇÃO CRÍTICA: Atualiza estado da UI para garantir consistência
      if (currentlySelectedId !== component.id) {
        this.uiState.selectComponent(component.id);
      }
      
      this.currentComponent = component;
      this.quickEditProperties = this.getQuickEditProperties(component);
      
      // Calcula posição
      const componentBounds = this.positionCalculator.getComponentBounds(element);
      const viewportBounds = this.positionCalculator.getViewportBounds();
      
      // Posiciona editor inline próximo à localização do clique
      const optimalPosition = this.calculateInlinePosition(
        { x: event.clientX, y: event.clientY },
        componentBounds,
        viewportBounds
      );

      this.editorPosition = optimalPosition;
      this.showInlineEditor = true;
      this.editorOpenTime = Date.now(); // Rastreia quando o editor foi aberto
      
      this.cdr.detectChanges();
      
      // Foca primeiro input após um pequeno delay para garantir que ViewChild seja inicializado
      setTimeout(() => {
        this.focusFirstInput();
      }, 200);
    });
  }

  private getQuickEditProperties(component: BuilderNode): QuickEditProperty[] {
    const props: QuickEditProperty[] = [];
    const componentProps = component.props as any;

    // Obtém propriedades essenciais baseadas no tipo de componente
    switch (component.type) {
      case 'text':
      case 'textarea':
        props.push(
          { key: 'label', displayName: 'Label', type: 'text', value: componentProps.label || '' },
          { key: 'placeholder', displayName: 'Placeholder', type: 'text', value: componentProps.placeholder || '' },
          { key: 'required', displayName: 'Required', type: 'boolean', value: componentProps.required || false }
        );
        break;

      case 'button':
        props.push(
          { key: 'text', displayName: 'Text', type: 'text', value: componentProps.text || '' },
          { 
            key: 'buttonType', 
            displayName: 'Type', 
            type: 'select', 
            value: componentProps.buttonType || 'default',
            options: [
              { label: 'Default', value: 'default' },
              { label: 'Primary', value: 'primary' },
              { label: 'Danger', value: 'danger' },
            ]
          },
          { 
            key: 'size', 
            displayName: 'Size', 
            type: 'select', 
            value: componentProps.size || 'default',
            options: [
              { label: 'Small', value: 'small' },
              { label: 'Default', value: 'default' },
              { label: 'Large', value: 'large' },
            ]
          }
        );
        break;

      case 'number':
        props.push(
          { key: 'label', displayName: 'Label', type: 'text', value: componentProps.label || '' },
          { key: 'min', displayName: 'Min', type: 'number', value: componentProps.min || 0 },
          { key: 'max', displayName: 'Max', type: 'number', value: componentProps.max || 100 },
          { key: 'required', displayName: 'Required', type: 'boolean', value: componentProps.required || false }
        );
        break;

      case 'switch':
        props.push(
          { key: 'label', displayName: 'Label', type: 'text', value: componentProps.label || '' },
          { key: 'defaultValue', displayName: 'Default', type: 'boolean', value: componentProps.defaultValue || false }
        );
        break;
    }

    // Adiciona propriedades de cor comuns para todos os componentes
    if (this.hasColorProperties(component.type)) {
      props.push(
        { key: 'backgroundColor', displayName: 'Background', type: 'color', value: componentProps.backgroundColor || '#ffffff' },
        { key: 'textColor', displayName: 'Text Color', type: 'color', value: componentProps.textColor || '#000000' }
      );
    }

    return props;
  }

  private hasColorProperties(componentType: string): boolean {
    return ['text', 'textarea', 'button', 'number', 'switch'].includes(componentType);
  }

  private calculateInlinePosition(
    clickPosition: { x: number; y: number },
    componentBounds: any,
    viewportBounds: any
  ): PanelPosition {
    const editorWidth = 280;
    const editorHeight = Math.min(200, this.quickEditProperties.length * 60 + 80);
    
    let x = clickPosition.x + 10;
    let y = clickPosition.y - editorHeight / 2;

    // Ajusta se estiver saindo da tela
    if (x + editorWidth > viewportBounds.width) {
      x = clickPosition.x - editorWidth - 10;
    }
    
    if (y < 10) {
      y = 10;
    } else if (y + editorHeight > viewportBounds.height - 10) {
      y = viewportBounds.height - editorHeight - 10;
    }

    return { x, y, width: editorWidth, height: editorHeight };
  }

  private isClickInsideEditor(event: MouseEvent): boolean {
    const target = event.target as HTMLElement;
    
    // Verificação de segurança adicional
    if (!target) return false;
    
    // Verifica se o clique está dentro do overlay do editor inline
    const editorOverlay = target.closest('.inline-editor-overlay');
    if (editorOverlay) {
      return true;
    }
    
    // Verifica se o clique está dentro do elemento do card do editor
    if (this.editorCard?.nativeElement) {
      const editorElement = this.editorCard.nativeElement;
      if (editorElement.contains(target)) {
        return true;
      }
    }
    
    // Verifica elementos de overlay do NG-Zorro (dropdowns, color pickers, etc.)
    // Estes são frequentemente renderizados fora da árvore DOM principal
    const overlaySelectors = [
      '.cdk-overlay-container',
      '.ant-select-dropdown',
      '.ant-color-picker-panel',
      '.ant-picker-dropdown',
      '.ant-tooltip',
      '.ant-popover',
      '.ant-dropdown',
      '.cdk-overlay-pane',
      '.ant-color-picker',
      '.ant-color-picker-trigger'
    ];
    
    for (const selector of overlaySelectors) {
      const overlayElement = target.closest(selector);
      if (overlayElement) {
        return true;
      }
    }
    
    // Verifica se o alvo tem classes relacionadas ao NG-Zorro
    const targetClasses = target.className;
    if (typeof targetClasses === 'string') {
      const nzClasses = [
        'ant-',
        'cdk-',
        'ng-',
        'nz-'
      ];
      
      for (const nzClass of nzClasses) {
        if (targetClasses.includes(nzClass)) {
          return true;
        }
      }
    }
    
    return false;
  }

  private focusFirstInput() {
    if (!this.editorCard?.nativeElement) return;
    
    try {
      const firstInput = this.editorCard.nativeElement.querySelector('input, select') as HTMLElement;
      if (firstInput) {
        firstInput.focus();
      }
    } catch (error) {
      console.warn('Error focusing first input:', error);
    }
  }

  // Métodos públicos para template
  updatePropertyInline(property: QuickEditProperty, value: any) {
    if (!this.currentComponent) return;

    const updatedProps = {
      ...this.currentComponent.props,
      [property.key]: value,
    };

    const updatedComponent: BuilderNode = {
      ...this.currentComponent,
      props: updatedProps,
    };

    this.builderService.updateNode(updatedComponent);
    
    // Atualiza valor da propriedade local
    property.value = value;
  }

  openFullEditor() {
    if (this.currentComponent) {
      this.uiState.selectComponent(this.currentComponent.id);
      this.closeInlineEditor();
    }
  }

  closeInlineEditor() {
    this.showInlineEditor = false;
    this.currentComponent = null;
    this.quickEditProperties = [];
    this.cdr.detectChanges();
  }

  // Atalhos de teclado
  onKeyDown(event: KeyboardEvent) {
    if (event.key === 'Enter' && (event.ctrlKey || event.metaKey)) {
      this.openFullEditor();
    } else if (event.key === 'Escape') {
      this.closeInlineEditor();
    }
  }

  // Função de rastreamento para ngFor
  trackByProperty(index: number, property: QuickEditProperty): string {
    return property.key;
  }
}
